import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase';

const UserContext = createContext(null);

// The signed-in reader. Identity is the account id; the name is a label that can
// change. Sessions saved before the id existed are topped up on load rather than
// being asked to sign in again.

export function UserProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('bible_app_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const persist = (userData) => {
    localStorage.setItem('bible_app_user', JSON.stringify(userData));
    setUser(userData);
  };

  // An older session carries name and email but no id. Look it up once and
  // keep it, so every write from here on can carry the account id.
  useEffect(() => {
    if (!user || user.id || !user.email) return;
    let cancelled = false;
    // Asked through a function because the browser can no longer read the
    // users table; it answers with the id alone.
    supabase.rpc('account_id_for_email', { p_email: user.email })
      .then(({ data: id }) => {
        if (cancelled || !id) return;
        persist({ ...user, id });
      });
    return () => { cancelled = true; };
  }, [user]);

  const login = (userData) => {
    localStorage.removeItem('bibleAppName');   // clear old legacy key
    persist(userData);
  };

  const logout = () => {
    localStorage.removeItem('bible_app_user');
    localStorage.removeItem('bibleAppName');
    // A Google session outlives this app's own, and the sign-in screen picks it
    // straight back up — so logging out has to end that one too, or the reader
    // never actually gets out.
    supabase.auth.signOut().catch(err => console.error('sign-out failed', err));
    setUser(null);
  };

  return (
    <UserContext.Provider value={{ user, login, logout }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
